import React, { useState, useEffect } from 'react';
import { usersAPI, messagesAPI } from '../utils/api';
import Avatar from './Avatar';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';

export default function CreateGroupModal({ onClose, onGroupCreated }) {
  const { isUserOnline } = useSocket();
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setLoading(true);
    usersAPI.getAll().then(r => setEmployees(r.data.data)).catch(() => toast.error('Failed to load employees.')).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filtered = employees.filter(e =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!groupName.trim()) { toast.error('Please enter a group name.'); return; }
    if (selected.length < 1) { toast.error('Select at least 1 member.'); return; }
    setCreating(true);
    try {
      const res = await messagesAPI.createGroup({ groupName: groupName.trim(), memberIds: selected });
      toast.success(res.data.message);
      onGroupCreated(res.data.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create group.');
    } finally { setCreating(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">Create Group</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Create a group conversation for your team</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Group Name */}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Group Name *</label>
            <input className="form-input" placeholder="e.g. Finance Team, Project Alpha…" value={groupName} onChange={e => setGroupName(e.target.value)} maxLength={60} />
          </div>

          {/* Search */}
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Add Members ({selected.length} selected)</label>
            <input className="form-input" placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {/* Selected chips */}
          {selected.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {selected.map(id => {
                const emp = employees.find(e => e._id === id);
                if (!emp) return null;
                return (
                  <span key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px 3px 6px', background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                    <Avatar user={emp} size="sm" />
                    {emp.name}
                    <span style={{ cursor: 'pointer', marginLeft: 2, fontWeight: 700 }} onClick={() => toggle(id)}>×</span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Employee List */}
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1.5px solid var(--border)', borderRadius: 8 }}>
            {loading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Loading employees…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No employees found.</div>
            ) : filtered.map(emp => (
              <div key={emp._id}
                onClick={() => toggle(emp._id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-soft)', background: selected.includes(emp._id) ? 'var(--brand-light)' : 'transparent', transition: 'background 0.15s' }}>
                <input type="checkbox" checked={selected.includes(emp._id)} onChange={() => {}} style={{ accentColor: 'var(--brand)', width: 15, height: 15, flexShrink: 0 }} />
                <Avatar user={emp} size="sm" showStatus isOnline={isUserOnline(emp._id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{emp.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{emp.department}{emp.designation ? ` · ${emp.designation}` : ''}</div>
                </div>
                <span style={{ fontSize: 11, color: isUserOnline(emp._id) ? 'var(--online)' : 'var(--offline)', fontWeight: 500 }}>
                  {isUserOnline(emp._id) ? '● Online' : '● Offline'}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !groupName.trim() || selected.length < 1}>
            {creating ? 'Creating…' : `Create Group (${selected.length + 1} members)`}
          </button>
        </div>
      </div>
    </div>
  );
}
